import { useState } from 'react';

import { useGetContext, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight, IconShieldCheck, IconZoomOutline } from '@iwoca/icons';
import { Accordion, Input, Link, Text } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';

import { TOnlineBankWithProvider } from './BankSelectionModal.types';
import { getBanksFromContext } from './getBanksFromContext';
import { LinkedAccount } from './getLinkedAccounts';
import { useOpenShareableLinkSplitTest } from './useOpenShareableLinkSplitTest';
import { getCurrentPathname } from '../../../iwRouter/LocationHelper';
import { LinkedAccountsList } from '../../ui/linkedAccount/LinkedAccountsList';
import { ShareableLinkModal } from '../shareableLinkModal/ShareableLinkModal';

export const ChooseBank = ({
    selectBank,
    linkingForBankVerification = false,
    isBankEvidence = false,
    linkedAccounts = []
}: {
    selectBank: (bank: TOnlineBankWithProvider) => void;
    linkingForBankVerification?: boolean;
    isBankEvidence?: boolean;
    linkedAccounts?: LinkedAccount[];
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const intl = useIntl();
    const { isInOpenShareableLinkSplitTest } = useOpenShareableLinkSplitTest();
    const isNewFundingRequirements = useSplitTests(
        'new_customer_funding_requirements_q2_2026'
    );

    return (
        <div
            className={
                isNewFundingRequirements
                    ? 'rounded-s border border-secondary-90'
                    : undefined
            }
        >
            <div className={isNewFundingRequirements ? 'p-xl pt-s' : undefined}>
                {linkedAccounts.length > 0 && (
                    <LinkedAccountsList accounts={linkedAccounts} />
                )}
                {isBankEvidence ? (
                    <BankEvidenceSelectionCopy />
                ) : linkingForBankVerification ? (
                    <VerificationBankSelectionCopy />
                ) : (
                    <RegularBankSelectionCopy />
                )}
                <div className="space-y-l">
                    <Input
                        iconLeft={<IconZoomOutline />}
                        type="text"
                        placeholder={intl.formatMessage({
                            id: '_.bank_selection_modal.search_placeholder',
                            defaultMessage: 'Search for your bank...'
                        })}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <ListOfBanks
                        searchQuery={searchQuery}
                        selectBank={selectBank}
                    />
                </div>
            </div>
            {isInOpenShareableLinkSplitTest && <OpenShareableLinkEntry />}
        </div>
    );
};

const OpenShareableLinkEntry = () => {
    const [isShareableLinkModalOpen, setIsShareableLinkModalOpen] =
        useState(false);
    const isNewFundingRequirements = useSplitTests(
        'new_customer_funding_requirements_q2_2026'
    );

    return (
        <>
            <div
                className={`border-t border-secondary-90 ${
                    isNewFundingRequirements ? 'px-xl py-l' : 'pt-l'
                }`}
            >
                <Link
                    className="inline-flex items-center gap-xs text-s font-med"
                    onClick={() => {
                        tracking.ampli.ctaClick({
                            'CTA text': 'Share a link with someone who does',
                            URL: getCurrentPathname()
                        });
                        setIsShareableLinkModalOpen(true);
                    }}
                >
                    <FormattedMessage
                        id="_.bank_selection_modal.shareable_link_entry"
                        defaultMessage="Don’t know your login and need help linking your account?"
                    />
                    <IconAngleRight className="size-l" />
                </Link>
            </div>
            <ShareableLinkModal
                isOpen={isShareableLinkModalOpen}
                onClose={() => setIsShareableLinkModalOpen(false)}
            />
        </>
    );
};

const VerificationBankSelectionCopy = () => {
    return (
        <>
            <div className="space-y-l">
                <Text>
                    <FormattedMessage
                        id="_.bank_selection_modal.verification_description"
                        defaultMessage="Select your bank and then securely log in through your bank's portal when prompted. Open Banking shares your account number, your name and the latest 30 days of transactions."
                    />
                </Text>
                <div className="flex flex-row justify-end">
                    <Text className="inline-flex items-center gap-s text-s font-med text-success-drk">
                        <IconShieldCheck />
                        <FormattedMessage
                            id="_.bank_selection_modal.bank_grade_encryption"
                            defaultMessage="Bank-grade encryption"
                        />
                    </Text>
                </div>
            </div>
        </>
    );
};

const HowDoesItWorkAccordion = ({
    description
}: {
    description: React.ReactNode;
}) => (
    <Accordion.Item variant="plain" size="small" defaultOpen={false}>
        <Accordion.Header>
            <Accordion.Title className="!pl-0">
                <span className="inline-flex items-center gap-s">
                    <IconShieldCheck className="shrink-0" />
                    <Text className="text-s">
                        <FormattedMessage
                            id="_.bank_selection_modal.fastest_and_secure"
                            defaultMessage="Fastest and most secure way to get a decision."
                        />{' '}
                        <span className="font-med text-primary-40">
                            <FormattedMessage
                                id="_.bank_selection_modal.how_does_it_work"
                                defaultMessage="How does it work?"
                            />
                        </span>
                    </Text>
                </span>
            </Accordion.Title>
            <Accordion.Trigger />
        </Accordion.Header>
        <Accordion.Content>
            <Text className="text-s">{description}</Text>
        </Accordion.Content>
    </Accordion.Item>
);

const BankEvidenceSelectionCopy = () => (
    <HowDoesItWorkAccordion
        description={
            <FormattedMessage
                id="_.bank_selection_modal.uk_bank_evidence_description"
                defaultMessage="Securely share your bank account data. Choose your bank below and log in through your bank’s portal when prompted."
            />
        }
    />
);

const RegularBankSelectionCopy = () => (
    <HowDoesItWorkAccordion
        description={
            <FormattedMessage
                id="_.bank_selection_modal.description"
                defaultMessage="Securely share your bank account’s data. Simply choose your bank below and then log in through your bank’s portal when prompted."
            />
        }
    />
);

const ListOfBanks = ({
    searchQuery,
    selectBank
}: {
    searchQuery: string;
    selectBank: (bank: TOnlineBankWithProvider) => void;
}) => {
    const { data: context } = useGetContext();

    const banks = getBanksFromContext(context);
    const isUK = getCountry() === 'UK';
    const popularBanks = getPopularUkBanks(banks);
    const showPopularBanksOnly =
        isUK && !searchQuery && popularBanks.length > 0;
    const bankSource = showPopularBanksOnly ? popularBanks : banks;
    const filteredBanks = bankSource.filter((bank) =>
        bank.bank_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!filteredBanks || filteredBanks.length === 0)
        return (
            <div>
                <Text>
                    <FormattedMessage
                        id="_.bank_selection_modal.no_banks_available"
                        defaultMessage="Unfortunately the bank you have chosen is not supported."
                    />
                </Text>
            </div>
        );

    return (
        <div className="space-y-m">
            <Text
                className="text-content-40 text-s font-bold"
                aria-hidden={!showPopularBanksOnly}
            >
                {showPopularBanksOnly ? (
                    <FormattedMessage
                        id="_.bank_selection_modal.most_popular_banks"
                        defaultMessage="Most popular banks"
                    />
                ) : (
                    ' '
                )}
            </Text>
            <div className="grid grid-cols-4 gap-m min-sm:grid-cols-7">
                {filteredBanks.map((bank) => (
                    <BankTile
                        key={bank.bank_id}
                        bank={bank}
                        onSelect={() => selectBank(bank)}
                    />
                ))}
            </div>
        </div>
    );
};

const BankTile = ({
    bank,
    onSelect
}: {
    bank: TOnlineBankWithProvider;
    onSelect: () => void;
}) => (
    <button className="flex flex-col items-center gap-xs" onClick={onSelect}>
        <img
            data-testid={`bank-image-${bank.bank_name}`}
            src={bank.logo_url}
            alt={bank.bank_name}
            className="fs-mask dd-privacy-mask h-[40px] w-[40px] rounded-full object-contain p-xs"
        />
        <span className="text-center text-xs">{bank.bank_name}</span>
    </button>
);

const POPULAR_UK_BANK_NAMES = [
    'Monzo',
    'Natwest',
    'Lloyds',
    'Barclays',
    'Revolut',
    'Starling',
    'Tide'
];

const getPopularUkBanks = (
    banks: TOnlineBankWithProvider[]
): TOnlineBankWithProvider[] =>
    POPULAR_UK_BANK_NAMES.flatMap((name) => {
        const match = banks.find((bank) =>
            bank.bank_name.toLowerCase().includes(name.toLowerCase())
        );
        return match ? [match] : [];
    });
